import { useEffect } from 'react';
import { getProductImageUrl } from '../../utils/productImages';
import { getProductPath } from '../../utils/productUrls';

const SITE_URL = 'https://www.valleyintlsupply.com';

function setMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
}

export default function ProductSeo({ product }) {
  useEffect(() => {
    if (!product?.product_name) return undefined;

    const canonical = `${SITE_URL}${getProductPath(product, { trailingSlash: true })}`;
    const rawDescription = String(product.product_description || `${product.product_name} industrial product from ${product.product_brand || 'Valley International Supply'}.`)
      .replace(/\s+/g, ' ')
      .trim();
    const description = rawDescription.length > 160
      ? `${rawDescription.slice(0, 157).replace(/\s+\S*$/, '')}...`
      : rawDescription;
    const title = `${product.product_name} | ${product.product_brand || 'Industrial Product'}`.slice(0, 65);
    const image = getProductImageUrl(product.product_image);
    const price = Number(product.product_price);
    const stock = Number(product.product_stock) || 0;
    const documents = [product.product_pdf, product.product_pdf2, product.product_pdf3]
      .filter((value) => value && !['none', 'null'].includes(String(value).toLowerCase()))
      .map((value, index) => ({
        '@type': 'DigitalDocument',
        name: `Technical document ${index + 1} for ${product.product_name}`,
        url: /^https?:\/\//i.test(value) ? value : `https://valleyapi-dr9y.onrender.com/data/${String(value).replace(/^\/+/, '')}`,
        encodingFormat: 'application/pdf'
      }));

    document.title = title;
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', canonical);
    setMeta('meta[name="description"]', { name: 'description', content: description });
    setMeta('meta[name="robots"]', { name: 'robots', content: 'index, follow, max-image-preview:large' });
    setMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    setMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    setMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });
    setMeta('meta[property="og:type"]', { property: 'og:type', content: 'product' });
    setMeta('meta[property="og:image"]', { property: 'og:image', content: image });
    setMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    setMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    setMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    setMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: image });
    setMeta('meta[name="twitter:url"]', { name: 'twitter:url', content: canonical });
    document.getElementById('valley-webpage-schema')?.remove();

    const productSchema = {
      '@type': 'Product',
      '@id': `${canonical}#product`,
      url: canonical,
      mainEntityOfPage: canonical,
      name: product.product_name,
      sku: String(product.idproducts),
      description,
      image: [image],
      brand: {
        '@type': 'Brand',
        name: product.product_brand || 'Unspecified'
      },
      category: product.product_category || undefined,
      subjectOf: documents.length ? documents : undefined,
      additionalProperty: [
        {
          '@type': 'PropertyValue',
          name: 'Category',
          value: product.product_category || 'Industrial products'
        },
        {
          '@type': 'PropertyValue',
          name: 'Available quantity',
          value: String(stock)
        }
      ],
      offers: Number.isFinite(price) && price > 0 ? {
        '@type': 'Offer',
        url: canonical,
        priceCurrency: 'USD',
        price: price.toFixed(2),
        availability: stock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        itemCondition: 'https://schema.org/NewCondition',
        seller: {
          '@type': 'Organization',
          name: 'Valley International Supply'
        }
      } : undefined
    };

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        productSchema,
        {
          '@type': 'BreadcrumbList',
          '@id': `${canonical}#breadcrumb`,
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
            { '@type': 'ListItem', position: 2, name: 'Products', item: `${SITE_URL}/search?q=*` },
            { '@type': 'ListItem', position: 3, name: product.product_name, item: canonical }
          ]
        }
      ]
    };

    let script = document.getElementById('valley-product-schema');
    if (!script) {
      script = document.createElement('script');
      script.id = 'valley-product-schema';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);

    return () => document.getElementById('valley-product-schema')?.remove();
  }, [product]);

  return null;
}
